import { ColDef, ColGroupDef, ColumnState } from "ag-grid-community";
import { TQC2070_R01Dto } from "../Dto_TQC2070_R01";
import { getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { aggridHeaderClass } from "src/components/common/common";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { formatGridNumber2 } from "src/utils/number/numberUtils";
import { formatDate } from "src/utils/string/stringUtil";

/**
 * 헤더 이름 매핑
 */
export const headerNames: Record<string, string> = {
	rowIndex: "",
	analysisDt: "일자",
	znAm: '아연액분석 오전',
	znPm: '아연액분석 오후',
	naohAm: '가성소다 오전검침',
	naohPm: '가성소다 오후검침',
	znInputQty: '아연 투입량(kg)',
	naohInputQty: '가성소다 투입량(kg)',
	znReanalysisValue: '아연',
	naohReanalysisValue: '가성소다',
	remark: '비고'
};

/**
 * 기본 컬럼 정의 생성 함수
 */
export const createBaseColumnDefs = (
	renderIndexCell: (params: any) => React.ReactElement,
	cellDateComparator: (valueA: Date, valueB: Date) => number
): (ColDef<TQC2070_R01Dto> | ColGroupDef<TQC2070_R01Dto>)[] => {
	return [
		...getDefaultRowIndexColumnConfig(renderIndexCell),
		{
			headerName: '일자',
			colId: 'analysisDt',
			field: 'analysisDt',
			width: 120,
			valueFormatter: (params: any) => {
				if (!params.data?.analysisDt) return '';
				return formatDate(new Date(params.data.analysisDt));
			},
			comparator: cellDateComparator,
			editable: true,
		},
		{
			headerName: '아연 액분석',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: 'AM 09:00',
					colId: 'znAm',
					field: 'znAm',
					width: 130,
					valueFormatter: formatGridNumber2,
					type: 'numericColumn',
					editable: true,
				},
				{
					headerName: 'PM 1:00',
					colId: 'znPm',
					field: 'znPm',
					width: 130,
					valueFormatter: formatGridNumber2,
					type: 'numericColumn',
					editable: true,
				},
			],
		},
		{
			headerName: '가성소다 액분석',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: 'AM 09:00',
					colId: 'naohAm',
					field: 'naohAm',
					width: 140,
					valueFormatter: formatGridNumber2,
					type: 'numericColumn',
					editable: true,
				},
				{
					headerName: 'PM 1:00',
					colId: 'naohPm',
					field: 'naohPm',
					width: 140,
					valueFormatter: formatGridNumber2,
					type: 'numericColumn',
					editable: true,
				},
			],
		},
		{
			headerName: '조치사항(kg)',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: '아연 투입량',
					colId: 'znInputQty',
					field: 'znInputQty',
					width: 120,
					valueFormatter: formatGridNumber2,
					type: 'numericColumn',
					editable: true,
				},
				{
					headerName: '가성소다 투입량',
					colId: 'naohInputQty',
					field: 'naohInputQty',
					width: 130,
					valueFormatter: formatGridNumber2,
					type: 'numericColumn',
					editable: true,
				},
			],
		},
		{
			headerName: '재분석',
			headerClass: 'common-group-header',
			children: [
				{
					headerName: '아연',
					colId: 'znReanalysisValue',
					field: 'znReanalysisValue',
					width: 110,
					valueFormatter: formatGridNumber2,
					type: 'numericColumn',
					editable: true,
				},
				{
					headerName: '가성소다',
					colId: 'naohReanalysisValue',
					field: 'naohReanalysisValue',
					width: 110,
					valueFormatter: formatGridNumber2,
					type: 'numericColumn',
					editable: true,
				},
			],
		},
		{
			headerName: '비고',
			colId: 'remark',
			field: 'remark',
			width: 200,
			editable: true,
		},
	];
};

/**
 * 그룹 헤더 구조를 유지하면서 헤더 클래스 정규화
 */
const normalizeHeaderClass = (
	defs: (ColDef<TQC2070_R01Dto> | ColGroupDef<TQC2070_R01Dto>)[]
): (ColDef<TQC2070_R01Dto> | ColGroupDef<TQC2070_R01Dto>)[] => {
	return defs.map((def: any) => {
		// children이 있으면 재귀적으로 처리
		const children = Array.isArray(def.children) && def.children.length > 0
			? normalizeHeaderClass(def.children)
			: undefined;

		if (children) {
			// ColGroupDef인 경우 - 공통 그룹 헤더 클래스만 유지
			const groupHeaderClass = [def.headerClass, 'common-group-header']
				.filter(Boolean)
				.join(' ');
			return {
				...def,
				headerClass: groupHeaderClass,
				suppressMenu: true,
				children
			} as ColGroupDef<TQC2070_R01Dto>;
		} else {
			// ColDef인 경우 - 일반 헤더는 aggridHeaderClass 적용
			const mergedHeaderClass = [aggridHeaderClass, def.headerClass]
				.filter(Boolean)
				.join(' ');
			return {
				...def,
				headerClass: mergedHeaderClass,
				suppressMenu: true
			} as ColDef<TQC2070_R01Dto>;
		}
	}).filter(Boolean) as (ColDef<TQC2070_R01Dto> | ColGroupDef<TQC2070_R01Dto>)[];
};

/**
 * 최종 컬럼 정의 생성 (컬럼 상태 병합 포함)
 */
export const createColumnDefs = (
	baseColumnDefs: (ColDef<TQC2070_R01Dto> | ColGroupDef<TQC2070_R01Dto>)[],
	columnState: ColumnState[] | null
): (ColDef<TQC2070_R01Dto> | ColGroupDef<TQC2070_R01Dto>)[] => {
	// analysisDt 컬럼을 제거한 baseColumnDefs 사용
	// analysisDt 컬럼은 이미 baseColumnDefs에 포함되어 있으므로 필터링 불필요
	const filteredBaseColumnDefs = baseColumnDefs;

	const mergedColumnDefs = GridCustomColumnDefs<TQC2070_R01Dto>({
		baseColumnDefs: filteredBaseColumnDefs,
		columnState: columnState || null,
		headerNames,
		fallbackHeaderClass: aggridHeaderClass
	});

	return normalizeHeaderClass(mergedColumnDefs);
};

